/**
 * NotificationsPanel — dropdown del bell con lista paginada de notifs.
 *
 * Doc 21 Fase Notif-1.e. Comportamiento:
 *
 * - Lista las N más recientes vía `GET /me/notifications`.
 * - Click en un item: lo marca como leído + navega al subject
 *   (artifact / project / etc. según event_type).
 * - "Marcar todo como leído": botón al header del panel — invoca
 *   `POST /me/notifications/mark_all_read`.
 * - Empty state amigable cuando no hay notifs.
 * - Loading state mientras carga.
 *
 * Estilo: dropdown fijo a la derecha del bell, ancho 380px,
 * max-height 480px con scroll interno si hay muchos items.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { CheckCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem as NotificationItemType,
} from '../../api/notifications'
import { useAccessToken } from '../../auth/useAccessToken'
import { NotificationItem } from './NotificationItem'

interface NotificationsPanelProps {
  onClose: () => void
}

export function NotificationsPanel({ onClose }: NotificationsPanelProps) {
  const { t } = useTranslation('shell')
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const listQuery = useQuery({
    queryKey: ['me', 'notifications', 'list'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchNotifications(accessToken, { limit: 20 })
    },
    enabled: !!accessToken,
    staleTime: 10_000,
  })

  const markReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!accessToken) throw new Error('no_access_token')
      return markNotificationRead(accessToken, notificationId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['me', 'notifications'],
      })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return markAllNotificationsRead(accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['me', 'notifications'],
      })
    },
  })

  function handleItemClick(item: NotificationItemType) {
    // Marca como leído (idempotente — backend no falla si ya estaba leído).
    if (!item.read_at) {
      markReadMutation.mutate(item.id)
    }
    // Navega al subject. URL se calcula del payload denormalizado.
    const url = resolveSubjectUrl(item)
    if (url) {
      void navigate({ to: url })
    }
    onClose()
  }

  const items = listQuery.data?.items ?? []
  const hasItems = items.length > 0
  const hasUnread = items.some((n) => n.read_at === null)

  return (
    <div
      role="dialog"
      aria-label={t('notifications.title')}
      className="absolute right-0 mt-2 w-[380px] max-h-[480px] bg-surface border border-line rounded-md shadow-lg z-50 flex flex-col"
    >
      {/* Header */}
      <header className="px-4 py-3 border-b border-line flex items-center justify-between shrink-0">
        <h3 className="font-display text-sm font-semibold text-ink">
          {t('notifications.title')}
        </h3>
        {hasUnread && (
          <button
            type="button"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-brand transition-colors disabled:opacity-50"
            title={t('notifications.mark_all_read')}
          >
            <CheckCheck className="w-3 h-3" />
            {t('notifications.mark_all_read')}
          </button>
        )}
      </header>

      {/* Body: lista o estados */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {listQuery.isLoading && (
          <div className="px-4 py-8 text-center text-xs text-ink-subtle font-mono">
            {t('notifications.loading')}
          </div>
        )}
        {listQuery.error && (
          <div className="px-4 py-8 text-center text-xs text-critical">
            {t('notifications.error')}
          </div>
        )}
        {!listQuery.isLoading && !listQuery.error && !hasItems && (
          <div className="px-4 py-12 text-center">
            <p className="text-sm text-ink-muted">
              {t('notifications.empty')}
            </p>
          </div>
        )}
        {hasItems && (
          <ul role="list" className="divide-y divide-line">
            {items.map((item) => (
              <li key={item.id}>
                <NotificationItem
                  item={item}
                  onClick={() => handleItemClick(item)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer: link a settings */}
      <footer className="px-4 py-2 border-t border-line shrink-0">
        <Link
          to="/settings/notifications"
          onClick={onClose}
          className="text-[11px] text-ink-muted hover:text-brand transition-colors"
        >
          {t('notifications.settings_link')}
        </Link>
      </footer>
    </div>
  )
}

/**
 * Resuelve la URL de navegación al hacer click en una notif.
 *
 * El mapeo depende del `event_type`:
 * - comment.* → artifact del comment (payload.project_id + artifact_id).
 * - stage.activated → mismo artifact (es tu turno de aprobar).
 * - artifact.rejected / canonized → el artifact.
 * - member.added_to_project → overview del proyecto.
 * - member.removed / raci_changed → home (ya no tiene proyecto o cambió rol).
 *
 * Devuelve null si no se puede resolver — el bell solo marca como leído sin navegar.
 */
function resolveSubjectUrl(item: NotificationItemType): string | null {
  const payload = item.payload || {}
  const projectId = typeof payload.project_id === 'string' ? payload.project_id : null
  const artifactId =
    typeof payload.artifact_id === 'string' ? payload.artifact_id : null

  // Comment events + stage + artifact events: navegar al artifact
  if (
    (item.event_type.startsWith('comment.') ||
      item.event_type.startsWith('artifact.') ||
      item.event_type === 'stage.activated') &&
    projectId &&
    artifactId
  ) {
    return `/projects/${projectId}/official/${artifactId}`
  }

  // Member added → overview del proyecto
  if (item.event_type === 'member.added_to_project' && projectId) {
    return `/projects/${projectId}/overview`
  }

  // Member raci_changed → tab equipo del proyecto
  if (item.event_type === 'member.raci_changed' && projectId) {
    return `/projects/${projectId}/team`
  }

  // member.removed → home (ya no tiene acceso)
  if (item.event_type === 'member.removed_from_project') {
    return '/'
  }

  return null
}
